import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import StatusDropdown from '@components/StatusDropdown/StatusDropdown';
import { StatusPill } from '@components/StatusPill';

import { isDevelopment } from '@constants/env';

import { useUpdatePatientProfileStatus } from '@hooks/api/vitall-patient';
import { useSession } from '@hooks/useSession';

import type { StatusModel } from 'generated/types';
import { StatusType } from 'generated/types';

const messages = defineMessages({
  label: {
    id: 'patientProfileStatus.label',
    defaultMessage: 'Profile Status',
  },
});

export default function ProfileStatusDropdown({
  vitall_patient_id,
  status,
  read_only,
  variant,
}: {
  /**
   * Status
   */
  status?: Pick<
    StatusModel,
    'color' | 'internal' | 'private_name' | 'public_name' | 'status_id'
  > | null;
  /**
   * Vitall patient ID associated with status
   */
  vitall_patient_id?: string | null;
  /**
   * Is the status read-only?
   */
  read_only?: boolean;
  /**
   * Variant of Status Pill
   */
  variant?: 'primary' | 'secondary';
}) {
  const {
    state: { isClient },
  } = useSession();

  const { formatMessage } = useIntl();
  const [updateProfileStatus, updateProfileStatusLoading] =
    useUpdatePatientProfileStatus();
  const loading = updateProfileStatusLoading;

  // Hide Inactive and Cancelled statuses from client
  if ((isClient && status?.internal) || (read_only && !status && !loading)) {
    return null;
  }

  if (read_only) {
    return (
      <ContentLoader loading={loading}>
        <StatusPill
          name={isClient ? status?.public_name : status?.private_name}
          color={status?.color ?? ''}
          variant={variant ?? 'primary'}
        />
      </ContentLoader>
    );
  }

  return (
    <StatusDropdown
      label={formatMessage(messages.label)}
      loading={loading}
      model={StatusType.VitallPatient_Profile}
      onChange={async (e) => {
        const { value } = e.target;
        try {
          if (!vitall_patient_id) throw new Error('No vitall patient');
          await updateProfileStatus({
            input: {
              vitall_patient_id,
              status_id: value,
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      }}
      value={status?.status_id ?? undefined}
      variant="secondary"
    />
  );
}
